import React, { FC, Fragment } from "react";
import { useTranslation } from "Providers/TranslationProvider";
import { ScheduleResource } from "domain-entities";
import { Box, Button, DialogContent } from "@material-ui/core";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import TextField from "Fields/TextField";
import { useSnackbar } from "material-ui-snackbar-provider";
import ModalTitle from "Components/Modal/ModalTitle";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import ModalActions from "Components/Modal/ModalActions";
import { useModalController } from "Components/Modal/Modal";
import TinyModal from "Components/Modal/TinyModal";
import { useApp } from "Providers/AppProvider";
import { useScheduleResources } from "./hooks/useScheduleResources";
import { verifyError } from "Components/Common/helpers/verifyError";
import { useScheduleResourceModal } from "./hooks/useScheduleResourceModal";

interface Props {
	scheduleResource: ScheduleResource;
}

interface FormValues {
	name: string;
}

const ScheduleResourceForm: FC<Props> = (props) => {
	const { t } = useTranslation();
	const { isNative } = useApp();
	const snackbar = useSnackbar();
	const deleteMC = useModalController();
	const isNewScheduleResource = useScheduleResourceModal(
		(state) => state.isNew
	);

	const defaultValues: FormValues = {
		name: props.scheduleResource.name,
	};

	const validationSchema = yup.object().shape({
		name: yup.string().required(
			t("Common_Form_Error_MandatoryField", {
				field: t("ScheduleResourceForm_Label_Name"),
			})
		),
	});

	const form = useForm<FormValues>({ validationSchema, defaultValues });
	const { control } = form;
	const isDisabled = form.formState.isSubmitting;

	async function onSubmit(formValues: FormValues) {
		try {
			await useScheduleResources.getState().setItem({
				id: props.scheduleResource.id,
				name: formValues.name,
			});
			useScheduleResourceModal.getState().close();
		} catch (error) {
			snackbar.showMessage(verifyError(error).message);
		}
	}

	return (
		<Fragment>
			<form onSubmit={form.handleSubmit(onSubmit)}>
				<ModalTitle
					close={useScheduleResourceModal.getState().close}
					title={
						isNewScheduleResource
							? t("ScheduleResourceForm_Title_Create")
							: t("ScheduleResourceForm_Title_Edit")
					}
				>
					<Button
						disabled={isDisabled}
						onClick={form.handleSubmit(onSubmit)}
						color="primary"
						variant="contained"
					>
						{isNewScheduleResource
							? t("Common_Dialog_Create")
							: t("Common_Dialog_Save")}
					</Button>
				</ModalTitle>
				<DialogContent>
					<Box mt={3}>
						<TextField
							name="name"
							label={t("ScheduleResourceForm_Label_Name")}
							control={control}
							autoFocus={!isNative}
						/>
					</Box>
				</DialogContent>
				{!isNewScheduleResource && (
					<ModalActions>
						<Button
							type="button"
							color="secondary"
							startIcon={<DeleteOutlineIcon />}
							disabled={isDisabled}
							onClick={deleteMC.open}
						>
							{t("Common_Dialog_Delete")}
						</Button>
					</ModalActions>
				)}
			</form>
			<TinyModal
				title={t("ScheduleResourceForm_Delete_Title")}
				modalController={deleteMC}
				actionLabel={t("Common_Dialog_Delete")}
				action={async () => {
					try {
						await useScheduleResources
							.getState()
							.deleteItem(props.scheduleResource);
						useScheduleResourceModal.getState().close();
					} catch (error) {
						snackbar.showMessage(verifyError(error).message);
					}
				}}
			>
				{t("Common_Dialog_DeleteMessage", { item: control.getValues().name })}
			</TinyModal>
		</Fragment>
	);
};

export default ScheduleResourceForm;
